import { useState } from "react"

import { Box, Button, Stack, Typography } from "@mui/material"
import { useQueryAdminDashboardClassSummary } from "api/reactQuery/queries/adminDashboard"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import { IDashboardPeriodParams } from "ts/interfaces/AdminDashboard"

import DashboardSection from "./DashboardSection"
import DashboardSectionBody, {
  RowsSkeleton,
  useSectionState,
} from "./DashboardSectionBody"
import PositivityChip from "./PositivityChip"
import RankedBarRow from "./RankedBarRow"

const COLLAPSED_ROWS = 5

const formatEarned = (value: number) =>
  `$${Math.round(value).toLocaleString("en-US")}`

const ClassSummary = ({ params }: { params: IDashboardPeriodParams }) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const query = useQueryAdminDashboardClassSummary(params)

  // BED sorts by total_earned desc and returns every row, so the cap is FE-side.
  const allRows =
    query.data?.data.rows.filter((row) => row.total_earned !== 0) ?? []
  const rows = expanded ? allRows : allRows.slice(0, COLLAPSED_ROWS)
  const top = rows[0]?.total_earned ?? 0
  const { state } = useSectionState([query], !allRows.length)

  return (
    <DashboardSection
      title={t("adminDashboard.classSummary")}
      question={t("adminDashboard.classSummaryQuestion")}
      exportImage={{
        section: "class-summary",
        dateFrom: query.data?.data.date_from,
        dateTo: query.data?.data.date_to,
        disabled: state !== "ready",
      }}
    >
      <Stack rowGap={2}>
        <DashboardSectionBody
          queries={[query]}
          isEmpty={!allRows.length}
          emptyText={t("adminDashboard.classSummaryEmpty")}
          skeleton={<RowsSkeleton rows={COLLAPSED_ROWS} />}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ color: "mockup.neutral30", textTransform: "uppercase" }}
          >
            {/* Skips RankedBarRow's 24px rank column + 16px gap */}
            <Typography variant="body3" sx={{ pl: 5 }}>
              {t("adminDashboard.classColumn")}
            </Typography>
            <Stack direction="row" gap={2}>
              <Typography
                variant="body3"
                sx={{ minWidth: 88, textAlign: "right" }}
              >
                {t("adminDashboard.bonusesFinesColumn")}
              </Typography>
              <Typography
                variant="body3"
                sx={{ minWidth: 72, textAlign: "right" }}
              >
                {t("adminDashboard.positivityColumn")}
              </Typography>
            </Stack>
          </Stack>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {rows.map((row, index) => (
              <RankedBarRow
                key={row.fk_class_id ?? index}
                rank={index + 1}
                label={row.name ?? "—"}
                ratio={top > 0 ? row.total_earned / top : 0}
                barColor="primary.main"
                right={
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Typography
                      variant="body3"
                      color="primary.main"
                      title={t("adminDashboard.earnedAmount", {
                        amount: formatEarned(row.total_earned),
                      })}
                      sx={{
                        minWidth: 88,
                        textAlign: "right",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      <span aria-hidden="true">
                        {row.bonus_count.toLocaleString("en-US")} ·{" "}
                        {row.fine_count.toLocaleString("en-US")}
                      </span>
                      {/* The hover title isn't reachable by keyboard or screen
                          readers, so the earned amount is spelled out here too */}
                      <Box component="span" sx={visuallyHiddenSx}>
                        {t("adminDashboard.classRowSummary", {
                          bonuses: row.bonus_count.toLocaleString("en-US"),
                          fines: row.fine_count.toLocaleString("en-US"),
                          amount: formatEarned(row.total_earned),
                        })}
                      </Box>
                    </Typography>
                    <Box sx={{ minWidth: 72, textAlign: "right" }}>
                      <PositivityChip positivity={row.positivity} />
                    </Box>
                  </Stack>
                }
              />
            ))}
          </Box>
          {allRows.length > COLLAPSED_ROWS && (
            <Stack alignItems="center" mt={1} data-export-ignore>
              <Button
                variant="text"
                aria-expanded={expanded}
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded
                  ? t("adminDashboard.showLess")
                  : t("adminDashboard.showAll", { count: allRows.length })}
              </Button>
            </Stack>
          )}
        </DashboardSectionBody>
      </Stack>
    </DashboardSection>
  )
}

export default ClassSummary
